import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/newTable";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import { StatusCell } from "./StatusCell";
import {
  StatusGridTableProps,
  StatusGridRow,
  DEFAULT_STATUS_CONFIG,
} from "./types";

export function StatusGridTable<T extends StatusGridRow>({
  data,
  fixedColumns,
  dateColumns,
  getStatus,
  onCellClick,
  title,
  isLoading = false,
  isFetching = false,
  emptyMessage = "Nenhum dado encontrado",
  statusConfig,
  highlightStatuses,
}: StatusGridTableProps<T>) {
  const mergedStatusConfig = { ...DEFAULT_STATUS_CONFIG, ...statusConfig };

  const shouldDimCell = (status: string | null): boolean => {
    if (!highlightStatuses || highlightStatuses.length === 0) return false;
    if (!status) return true;
    return !highlightStatuses.includes(status);
  };

  const showLoading = isLoading || isFetching;
  const totalColSpan = fixedColumns.length + dateColumns.length;

  return (
    <Card>
      {title && (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <ScrollArea className="w-full whitespace-nowrap rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#422534] hover:bg-[#422534]">
                {fixedColumns.map((col) => (
                  <TableHead
                    key={String(col.key)}
                    className="font-semibold text-white"
                    style={{ width: col.width }}
                  >
                    {col.header}
                  </TableHead>
                ))}
                {dateColumns.map((date) => (
                  <TableHead
                    key={date}
                    className="text-center font-semibold text-white min-w-[60px]"
                  >
                    {date}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {showLoading ? (
                <TableRow>
                  <TableCell colSpan={totalColSpan}>
                    <div className="flex items-center justify-center py-24">
                      <Loader2 className="h-8 w-8 animate-spin text-[#F4C83E]" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : data && data.length > 0 ? (
                data.map((row) => (
                  <TableRow key={row.id} className="hover:bg-muted/50 transition-colors">
                    {fixedColumns.map((col) => (
                      <TableCell
                        key={String(col.key)}
                        className={col.key === "id" ? "font-medium" : "whitespace-nowrap"}
                      >
                        {col.render
                          ? col.render(row[col.key], row)
                          : col.key === "regional"
                          ? (
                            <Badge variant="outline">
                              {String(row[col.key] ?? "-")}
                            </Badge>
                          )
                          : String(row[col.key] ?? "-")}
                      </TableCell>
                    ))}
                    {dateColumns.map((date) => {
                      const status = getStatus(row, date);
                      const dimmed = shouldDimCell(status);
                      return (
                        <TableCell
                          key={date}
                          className="p-1 text-center cursor-pointer"
                          onClick={() => onCellClick?.(row, date, status)}
                        >
                          <StatusCell
                            status={status}
                            statusConfig={mergedStatusConfig}
                            dimmed={dimmed}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={totalColSpan}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Legenda */}
        <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
          <span className="text-muted-foreground">Legenda:</span>
          {Object.entries(mergedStatusConfig).map(([key, config]) => (
            <div key={key} className="flex items-center gap-1">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: config.color }}
              />
              <span>
                {key === "OPENED"
                  ? "Aberta"
                  : key === "PENDING"
                  ? "Aguardando"
                  : key === "CLOSED"
                  ? "Fechada"
                  : "Não Integrada"}
              </span>
            </div>
          ))}
          <span className="text-muted-foreground ml-4 border-l pl-4">
            Clique no status para abrir a conciliação
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export * from "./types";
export { StatusCell } from "./StatusCell";
